/* Kashira pincode delivery-estimate checker.
   Honest, app-free: validates a 6-digit Indian pincode client-side and shows an
   ESTIMATED delivery window derived from the product's dispatch_days metafield
   plus a courier-transit range (block settings). It never claims a guaranteed
   date and makes no serviceability call — copy is passed in from locales. */
if (!customElements.get('kashira-pincode')) {
  /* Indian pincodes are six digits and never start with 0. */
  const PINCODE = /^[1-9][0-9]{5}$/;

  customElements.define(
    'kashira-pincode',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('.kashira-pincode__input');
        this.button = this.querySelector('.kashira-pincode__button');
        this.result = this.querySelector('.kashira-pincode__result');
        // The bespoke row draws this field's border, so it is the row — not the
        // input — that has to carry the invalid state and the shake (spec 17).
        this.row = this.querySelector('.kashira-pincode__row');
        if (!this.input || !this.button || !this.result) return;

        this.button.addEventListener('click', () => this.check());
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.check();
          }
        });
        // Numeric-only entry.
        this.input.addEventListener('input', () => {
          this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
          // Re-validate on input: the row clears the moment the pin is well
          // formed. Typing never raises a new error — CHECK is the submit here,
          // and blurring onto the CHECK button must not reject the entry.
          if (this.isInvalid && PINCODE.test(this.input.value)) this.clearInvalid();
        });
      }

      clearInvalid() {
        this.isInvalid = false;
        this.result.classList.remove('kashira-pincode__result--error');
        if (window.KashiraFieldState) window.KashiraFieldState.clear(this.input);
      }

      check() {
        const pin = (this.input.value || '').trim();
        if (!PINCODE.test(pin)) {
          this.show(`<span>${this.dataset.copyInvalid}</span>`, true);
          // Same field contract as every other surface: invalid class on the
          // border element, 300ms shake, the result paragraph reused as the
          // helper (it already owns the copy and the reveal), aria wiring, and
          // focus returned to the input.
          if (window.KashiraFieldState) {
            window.KashiraFieldState.invalid(this.input, { target: this.row, node: this.result });
          }
          this.isInvalid = true;
          this.input.focus();
          return;
        }
        this.clearInvalid();
        const dispatch = parseInt(this.dataset.dispatchDays, 10) || 2;
        const tMin = parseInt(this.dataset.transitMin, 10) || 2;
        const tMax = parseInt(this.dataset.transitMax, 10) || 5;
        const min = dispatch + tMin;
        const max = dispatch + tMax;
        // The estimate is not a serviceability check, so it says so: the note
        // is the promise the shipping policy makes (tell them in a business
        // day, refund in full) and is only shown when the copy is supplied.
        const note = this.dataset.copyNote
          ? `<span class="kashira-pincode__note">${this.dataset.copyNote}</span>`
          : '';
        this.show(
          `<strong>${this.dataset.copyLead} ${pin}</strong> · ${this.dataset.copyEstimate} ${min}–${max} ${this.dataset.copyDays}${note}`,
          false
        );
      }

      show(html, isError) {
        this.result.classList.toggle('kashira-pincode__result--error', isError);

        /* The reveal is a max-height/opacity transition, but the closed element
           is `display: none` — first from [hidden], then from base.css's
           `p:empty` rule — and a transition cannot start out of display:none.
           So: unhide, pin the closed values inline, write the copy (which ends
           `p:empty` and gives us a rendered box), force layout, then drop the
           inline values so the 200ms slide-down runs — and re-runs on every
           later check, which a one-way [hidden] flip never did. */
        this.result.hidden = false;
        this.result.style.maxHeight = '0';
        this.result.style.marginTop = '0';
        this.result.style.opacity = '0';
        this.result.innerHTML = html;
        void this.result.offsetHeight;
        this.result.style.removeProperty('max-height');
        this.result.style.removeProperty('margin-top');
        this.result.style.removeProperty('opacity');
      }
    }
  );
}
